import Alert from '../components/Alert';
import Loader from '../components/Loader';
import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import feather from 'feather-icons';
import { Link, useNavigate } from 'react-router-dom';
import useOutletRefreshSignal from '../hooks/useOutletRefreshSignal';
import { amountDisplay, dateDisplay } from '../util/helper';
import BottomNavbar from '../components/Home/BottomNavbar';
import BackButton from '../components/BackButton';
import dayjs from 'dayjs';
import serverClient from '../api/ServerClient';
import { DeleteToken } from '../auth/Auth';
import { useLanguage } from '../context/LanguageContext';
import { getPageMeta } from '../util/seoHelper';
import useRefreshSignal from '../hooks/useRefreshSignal';

// Renders a feather icon as inline SVG — avoids the React/feather DOM conflict
// that causes "removeChild: node is not a child" when icons are inside
// conditionally rendered sections.
const FeatherIcon = ({ name, size = 20, className = '' }) => (
    <span
        className={className}
        style={{ display: 'inline-flex', alignItems: 'center' }}
        dangerouslySetInnerHTML={{
            __html: feather.icons[name].toSvg({ width: size, height: size }),
        }}
    />
);

function Reports() {
    const navigate = useNavigate();
    const { language, t } = useLanguage();
    const [error, setError] = useState(null);
    const refreshSignal = useOutletRefreshSignal();
    const [loading, setLoading] = useState(false);
    const [statisticsData, setStatisticsData] = useState(null);
    const [totalBalance, setTotalBalance] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(dayjs());
    const [currentYear, setCurrentYear] = useState(dayjs());
    const [reportPeriod, setReportPeriod] = useState('monthly'); // 'monthly' | 'yearly'

    const fetchStatisticsData = useCallback(async () => {
        try {
            setLoading(true);
            setError(false);
            let url;
            if (reportPeriod === 'yearly') {
                const yearStart = currentYear.startOf('year').format('YYYY-MM-DD');
                url = `/api/user/reports?month=${yearStart}&reporting_type=yearly`;
            } else {
                const formattedMonth = currentMonth.format('YYYY-MM-01');
                url = `/api/user/reports?month=${formattedMonth}`;
            }
            const response = await serverClient.get(url);
            setStatisticsData(response?.data);
            setTotalBalance(response?.data?.totalBalance);
        } catch (e) {
            if (e.response?.status === 401) {
                DeleteToken();
                setError(t('userNotAuthorized'));
                navigate('/login');
            } else {
                setError(t('somethingWentWrong'));
            }
        } finally {
            setLoading(false);
        }
    }, [reportPeriod, currentYear, currentMonth, t, navigate]);

    const handlePrevMonth = () => setCurrentMonth((prev) => prev.subtract(1, 'month'));
    const handleNextMonth = () => setCurrentMonth((prev) => prev.add(1, 'month'));
    const handlePrevYear = () => setCurrentYear((prev) => prev.subtract(1, 'year'));
    const handleNextYear = () => setCurrentYear((prev) => prev.add(1, 'year'));
    const activeReportDate = reportPeriod === 'yearly' ? currentYear.startOf('year') : currentMonth;
    const categoryExpenseRoute = `/category-expense?month=${activeReportDate.format('YYYY-MM-01')}&type=${reportPeriod}`;
    const incomeReportRoute = `/report/income?month=${activeReportDate.format('YYYY-MM-01')}&type=${reportPeriod}`;

    useEffect(() => {
        // When toggling period, reset data and re-fetch with the new mode
        setStatisticsData(null);
    }, [reportPeriod]);

    // Re-fetch whenever the active period/mode changes.
    useEffect(() => {
        fetchStatisticsData();
        // only replace feather icons that are NOT inside dynamically toggled sections
        feather.replace();
    }, [fetchStatisticsData]);

    useRefreshSignal(refreshSignal, () => {
        fetchStatisticsData();
    });

    return (
        <>
            <Helmet>
                <title>{getPageMeta('reports', language).title}</title>
                <meta name="description" content={getPageMeta('reports', language).description} />
                <meta name="keywords" content={getPageMeta('reports', language).keywords} />
                <link rel="canonical" href={getPageMeta('reports', language).url} />
                {getPageMeta('reports', language).noindex && (
                    <meta name="robots" content="noindex, follow" />
                )}
                <meta property="og:type" content="website" />
                <meta property="og:title" content={getPageMeta('reports', language).title} />
                <meta
                    property="og:description"
                    content={getPageMeta('reports', language).description}
                />
                <meta property="og:url" content={getPageMeta('reports', language).url} />
                <meta property="og:image" content={getPageMeta('reports', language).image} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={getPageMeta('reports', language).title} />
                <meta
                    name="twitter:description"
                    content={getPageMeta('reports', language).description}
                />
                <meta name="twitter:image" content={getPageMeta('reports', language).image} />
            </Helmet>
            <div>
                {/* Header with settings dropdown */}
                <header className="section-t-space">
                    <div className="custom-container">
                        <div className="header-panel">
                            <BackButton />
                            <h2>{t('reports')}</h2>

                            <div className="dropdown">
                                <Link
                                    to="#"
                                    className="back-btn"
                                    role="button"
                                    data-bs-toggle="dropdown"
                                >
                                    {/* Static icon — safe to use data-feather here (not conditionally rendered) */}
                                    <i className="icon" data-feather="settings"></i>
                                </Link>

                                <ul className="dropdown-menu dropdown-menu-end">
                                    <li>
                                        <button
                                            className={`dropdown-item d-flex align-items-center gap-2${reportPeriod === 'monthly' ? ' fw-semibold text-primary' : ''}`}
                                            onClick={() => setReportPeriod('monthly')}
                                        >
                                            {/* Use Unicode check — avoids feather DOM conflict inside dropdown */}
                                            <span style={{ width: 16, fontSize: 12 }}>
                                                {reportPeriod === 'monthly' ? '✓' : ''}
                                            </span>
                                            {t('monthly')}
                                        </button>
                                    </li>
                                    <li>
                                        <button
                                            className={`dropdown-item d-flex align-items-center gap-2${reportPeriod === 'yearly' ? ' fw-semibold text-primary' : ''}`}
                                            onClick={() => setReportPeriod('yearly')}
                                        >
                                            <span style={{ width: 16, fontSize: 12 }}>
                                                {reportPeriod === 'yearly' ? '✓' : ''}
                                            </span>
                                            {t('yearly')}
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </header>

                <section>
                    <div className="custom-container section-b-space">
                        <div className="profile-section">
                            <div className="profile-banner">
                                <div className="profile-image">
                                    <img
                                        className="img-fluid profile-pic"
                                        src="/assets/images/icons/report_4.png"
                                        alt="Profile"
                                        referrerPolicy="no-referrer"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <Loader loading={loading} />
                {error && (
                    <div className="p-4">
                        <Alert alert={'danger'}>{error}</Alert>
                    </div>
                )}

                {statisticsData && (
                    <section className="section-b-space">
                        <div className="custom-container">
                            {error && <Alert alert={'danger'}>{error}</Alert>}

                            <div className="row g-3">
                                <div key="1" className="col-md-12 col-12">
                                    <div className="bill-box">
                                        <div className="gap-3">
                                            <Link
                                                to={
                                                    currentMonth.isSame(dayjs(), 'month')
                                                        ? `/report/balance`
                                                        : `/report/balance?month=${currentMonth.format('YYYY-MM-01')}`
                                                }
                                            >
                                                <div className="bill-icon float-end">
                                                    <img
                                                        className="img-fluid icon transaction-icon"
                                                        src={'assets/images/svg/balance-v2.svg'}
                                                        alt="balance"
                                                    />
                                                </div>
                                                <div className="bill-details">
                                                    <h4
                                                        className="light-text"
                                                        title={t('totalBalance')}
                                                    >
                                                        {t('totalBalance')}
                                                    </h4>
                                                    <h5 className={'dark-text mt-2'}>
                                                        <b>
                                                            {amountDisplay(totalBalance, language)}
                                                        </b>
                                                    </h5>
                                                </div>
                                            </Link>
                                        </div>
                                    </div>
                                </div>

                                <div key="2" className="col-md-12 col-12">
                                    <div className="bill-box">
                                        <div className="gap-3">
                                            <Link to={categoryExpenseRoute}>
                                                <div className="bill-icon float-end">
                                                    <img
                                                        className="img-fluid icon transaction-icon"
                                                        src={'assets/images/svg/spending-v2.svg'}
                                                        alt="spending"
                                                    />
                                                </div>
                                                <div className="bill-details">
                                                    <h4 className="light-text" title={t('expense')}>
                                                        {t('expense')}
                                                    </h4>
                                                    <h5 className={'mt-2 text-danger'}>
                                                        <b>
                                                            {amountDisplay(
                                                                statisticsData?.statistics
                                                                    ?.total_expenses,
                                                                language
                                                            )}
                                                        </b>
                                                    </h5>
                                                </div>
                                            </Link>
                                        </div>
                                    </div>
                                </div>

                                <div key="3" className="col-md-12 col-12">
                                    <div className="bill-box">
                                        <div className="gap-3">
                                            <Link
                                                to={`/report/cash-flow?month=${activeReportDate.format('YYYY-MM-01')}&type=${reportPeriod}`}
                                            >
                                                <div className="bill-icon float-end">
                                                    <img
                                                        className="img-fluid icon transaction-icon"
                                                        src={'assets/images/svg/net.svg'}
                                                        alt="Cash Flow"
                                                    />
                                                </div>
                                                <div className="bill-details">
                                                    <h4
                                                        className="light-text"
                                                        title={t('cashFlow')}
                                                    >
                                                        {t('cashFlow')}
                                                    </h4>
                                                    <h5
                                                        className={
                                                            statisticsData?.statistics
                                                                ?.net_balance < 0
                                                                ? 'mt-2 text-danger'
                                                                : 'dark-text mt-2'
                                                        }
                                                    >
                                                        <b>
                                                            {amountDisplay(
                                                                statisticsData?.statistics
                                                                    ?.net_balance,
                                                                language
                                                            )}
                                                        </b>
                                                    </h5>
                                                </div>
                                            </Link>
                                        </div>
                                    </div>
                                </div>

                                <div key="4" className="col-md-12 col-12">
                                    <div className="bill-box">
                                        <div className="gap-3">
                                            <Link
                                                to="/transaction-history"
                                                state={{
                                                    selectedCategoryId: 20,
                                                    selectedCategoryName: t('lending'),
                                                    periodType:
                                                        reportPeriod === 'yearly'
                                                            ? 'year'
                                                            : 'month',
                                                    reportingType: reportPeriod,
                                                    ...(reportPeriod === 'yearly' ||
                                                    !currentMonth.isSame(dayjs(), 'month')
                                                        ? {
                                                              startDate: (reportPeriod === 'yearly'
                                                                  ? currentYear
                                                                  : currentMonth
                                                              )
                                                                  .startOf(
                                                                      reportPeriod === 'yearly'
                                                                          ? 'year'
                                                                          : 'month'
                                                                  )
                                                                  .format('YYYY-MM-DD'),
                                                              endDate: (reportPeriod === 'yearly'
                                                                  ? currentYear
                                                                  : currentMonth
                                                              )
                                                                  .endOf(
                                                                      reportPeriod === 'yearly'
                                                                          ? 'year'
                                                                          : 'month'
                                                                  )
                                                                  .format('YYYY-MM-DD'),
                                                          }
                                                        : {}),
                                                }}
                                            >
                                                <div className="bill-icon float-end">
                                                    <img
                                                        className="img-fluid icon transaction-icon"
                                                        src={'assets/images/svg/lending-v2.svg'}
                                                        alt="lending"
                                                    />
                                                </div>
                                                <div className="bill-details">
                                                    <h4 className="light-text" title={t('lending')}>
                                                        {t('lending')}
                                                    </h4>
                                                    <h5 className={'mt-2 text-danger'}>
                                                        <b>
                                                            {amountDisplay(
                                                                statisticsData?.totalLending,
                                                                language
                                                            )}
                                                        </b>
                                                    </h5>
                                                </div>
                                            </Link>
                                        </div>
                                    </div>
                                </div>

                                <div key="5" className="col-md-12 col-12">
                                    <div className="bill-box">
                                        <div className="gap-3">
                                            <Link to={incomeReportRoute}>
                                                <div className="bill-icon float-end">
                                                    <img
                                                        className="img-fluid icon transaction-icon"
                                                        src={
                                                            'assets/images/svg/income-statistics.svg'
                                                        }
                                                        alt="income"
                                                    />
                                                </div>
                                                <div className="bill-details">
                                                    <h4 className="light-text" title={t('income')}>
                                                        {t('income')}
                                                    </h4>
                                                    <h5 className={'dark-text mt-2'}>
                                                        <b>
                                                            {amountDisplay(
                                                                statisticsData?.statistics
                                                                    ?.total_income,
                                                                language
                                                            )}
                                                        </b>
                                                    </h5>
                                                </div>
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Period navigator — uses FeatherIcon (inline SVG) to avoid React/feather DOM conflict */}
                <div className="fixed-month-selector">
                    {reportPeriod === 'monthly' ? (
                        <>
                            <button className="nav-button" onClick={handlePrevMonth}>
                                <FeatherIcon name="chevron-left" />
                            </button>
                            <div className="month-wrapper">
                                {dateDisplay(currentMonth, language, 'monthOnly')}
                            </div>
                            <button className="nav-button" onClick={handleNextMonth}>
                                <FeatherIcon name="chevron-right" />
                            </button>
                        </>
                    ) : (
                        <>
                            <button className="nav-button" onClick={handlePrevYear}>
                                <FeatherIcon name="chevron-left" />
                            </button>
                            <div className="month-wrapper">{currentYear.format('YYYY')}</div>
                            <button className="nav-button" onClick={handleNextYear}>
                                <FeatherIcon name="chevron-right" />
                            </button>
                        </>
                    )}
                </div>

                <BottomNavbar />
            </div>
        </>
    );
}

export default Reports;
